import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos ----------
interface PagoAsignacion {
  id: string;
  fecha: string;
  concepto: string;
  monto: number;
}

interface Asignacion {
  id: string;
  obraId: string;
  obraNombre: string;
  montoTotal: number;
  pagos: PagoAsignacion[];
}

interface Trabajador {
  id: string;
  nombre: string;
  puesto: string;
  rfc: string;
  nss: string;
  salarioDiario: number;
  fechaIngreso: string;
  asignaciones: Asignacion[];
}

interface TrabajadorForm {
  nombre: string;
  puesto: string;
  rfc: string;
  nss: string;
  salarioDiario: string;
  fechaIngreso: string;
}

interface AsignarForm {
  obraId: string;
  montoTotal: string;
}

interface PagoAsignacionForm {
  fecha: string;
  concepto: string;
  monto: string;
}

const emptyForm: TrabajadorForm = { nombre: '', puesto: '', rfc: '', nss: '', salarioDiario: '', fechaIngreso: new Date().toISOString().split('T')[0] };
const emptyAsignarForm: AsignarForm = { obraId: '', montoTotal: '' };
const emptyPagoForm: PagoAsignacionForm = { fecha: new Date().toISOString().split('T')[0], concepto: '', monto: '' };

function formatMonto(monto: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(monto);
}

// ---------- Helpers API ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function TrabajadoresPage() {
  const { showToast } = useToast();
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [obras, setObras] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal trabajador
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TrabajadorForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Trabajador | null>(null);

  // Modal asignar obra
  const [asignarModalOpen, setAsignarModalOpen] = useState(false);
  const [asignarTarget, setAsignarTarget] = useState<Trabajador | null>(null);
  const [asignarForm, setAsignarForm] = useState<AsignarForm>(emptyAsignarForm);

  // Confirmación eliminar asignación
  const [deleteAsignacionTarget, setDeleteAsignacionTarget] = useState<Asignacion | null>(null);
  const [deleteAsigWorker, setDeleteAsigWorker] = useState<Trabajador | null>(null);

  // Modal detalle de pagos de asignación
  const [detalleAsignacionOpen, setDetalleAsignacionOpen] = useState(false);
  const [detalleAsignacion, setDetalleAsignacion] = useState<{ trabajador: Trabajador; asignacion: Asignacion } | null>(null);
  const [pagoForm, setPagoForm] = useState<PagoAsignacionForm>(emptyPagoForm);

  // ---------- Carga inicial ----------
  const loadData = async () => {
    try {
      setLoading(true);
      const [trab, obrasData] = await Promise.all([
        fetchWithAuth('/trabajadores/'),
        fetchWithAuth('/todas'),
      ]);
      setTrabajadores(trab);
      // Solo mostramos obras activas en el selector de asignación
      setObras(obrasData.filter((o: any) => o.estado === 'activa'));
    } catch (err: any) {
      showToast('error', 'Error al cargar datos', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ---------- Filtrado ----------
  const filtered = useMemo(
    () =>
      trabajadores.filter(
        (t) =>
          t.nombre.toLowerCase().includes(search.toLowerCase()) ||
          t.puesto.toLowerCase().includes(search.toLowerCase()) ||
          t.rfc.toLowerCase().includes(search.toLowerCase())
      ),
    [trabajadores, search]
  );

  // ---------- Helpers de pagos ----------
  const totalPagado = (asig: Asignacion) => asig.pagos.reduce((sum, p) => sum + p.monto, 0);
  const saldoPendiente = (asig: Asignacion) => asig.montoTotal - totalPagado(asig);

  // ---------- Handlers de trabajador ----------
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (t: Trabajador) => {
    setEditingId(t.id);
    setForm({
      nombre: t.nombre,
      puesto: t.puesto,
      rfc: t.rfc,
      nss: t.nss,
      salarioDiario: t.salarioDiario.toString(),
      fechaIngreso: t.fechaIngreso,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    const payload = {
      nombre: form.nombre,
      puesto: form.puesto,
      rfc: form.rfc,
      nss: form.nss,
      salarioDiario: parseFloat(form.salarioDiario) || 0,
      fechaIngreso: form.fechaIngreso,
    };
    try {
      if (editingId) {
        const updated = await fetchWithAuth(`/trabajadores/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setTrabajadores((prev) => prev.map((t) => (t.id === editingId ? updated : t)));
        showToast('success', 'Trabajador actualizado');
      } else {
        const nuevo = await fetchWithAuth('/trabajadores/', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setTrabajadores((prev) => [...prev, nuevo]);
        showToast('success', 'Trabajador registrado');
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/trabajadores/${deleteTarget.id}`, { method: 'DELETE' });
      setTrabajadores((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      showToast('success', 'Trabajador eliminado');
      setDeleteTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  // ---------- Asignaciones ----------
  const openAsignar = (t: Trabajador) => {
    setAsignarTarget(t);
    setAsignarForm(emptyAsignarForm);
    setAsignarModalOpen(true);
  };

  const handleAsignarSave = async () => {
    if (!asignarTarget || !asignarForm.obraId || !asignarForm.montoTotal) return;
    const monto = parseFloat(asignarForm.montoTotal);
    if (isNaN(monto) || monto <= 0) return;

    try {
      const updated = await fetchWithAuth(
        `/trabajadores/${asignarTarget.id}/asignaciones?obraId=${asignarForm.obraId}&montoTotal=${monto}`,
        { method: 'POST' }
      );
      setTrabajadores((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      showToast('success', 'Trabajador asignado');
      setAsignarModalOpen(false);
      setAsignarTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al asignar', err.message);
    }
  };

  const handleEliminarAsignacion = async () => {
    if (!deleteAsignacionTarget || !deleteAsigWorker) return;
    try {
      const updated = await fetchWithAuth(
        `/trabajadores/${deleteAsigWorker.id}/asignaciones/${deleteAsignacionTarget.id}`,
        { method: 'DELETE' }
      );
      setTrabajadores((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      showToast('info', 'Asignación eliminada', `Se quitó "${deleteAsignacionTarget.obraNombre}"`);
      setDeleteAsignacionTarget(null);
      setDeleteAsigWorker(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar asignación', err.message);
    }
  };

  // ---------- Pagos ----------
  const openDetalleAsignacion = (trab: Trabajador, asig: Asignacion) => {
    setDetalleAsignacion({ trabajador: trab, asignacion: asig });
    setPagoForm(emptyPagoForm);
    setDetalleAsignacionOpen(true);
  };

  const handleAgregarPago = async () => {
    if (!detalleAsignacion || !pagoForm.monto || !pagoForm.concepto.trim()) return;
    const monto = parseFloat(pagoForm.monto);
    if (isNaN(monto) || monto <= 0) return;

    try {
      const updated = await fetchWithAuth(
        `/trabajadores/${detalleAsignacion.trabajador.id}/asignaciones/${detalleAsignacion.asignacion.id}/pagos`,
        {
          method: 'POST',
          body: JSON.stringify({
            fecha: pagoForm.fecha,
            concepto: pagoForm.concepto.trim(),
            monto: monto,
          }),
        }
      );
      setTrabajadores((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      // Actualizar el detalle actual para reflejar el nuevo pago
      const updatedAsig = updated.asignaciones.find((a: Asignacion) => a.id === detalleAsignacion.asignacion.id);
      if (updatedAsig) {
        setDetalleAsignacion({ trabajador: updated, asignacion: updatedAsig });
      }
      setPagoForm(emptyPagoForm);
      showToast('success', 'Pago registrado', `Se agregó ${formatMonto(monto)} a la asignación.`);
    } catch (err: any) {
      showToast('error', 'Error al registrar pago', err.message);
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto">
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Trabajadores</h1>
            <p className="text-sm text-foreground-500 mt-0.5">{trabajadores.length} trabajadores</p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line mr-1.5" />
            Nuevo trabajador
          </button>
        </div>

        {/* Búsqueda */}
        <div className="mb-4">
          <div className="relative max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, puesto o RFC..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-team-line text-xl text-foreground-300" />
              </div>
              <p className="text-sm text-foreground-500">No se encontraron trabajadores.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-background-100/50">
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3">Trabajador</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden md:table-cell">Puesto</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3">Obras asignadas</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Monto a Pagar</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden md:table-cell">Monto Pagado</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Salario diario</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Acciones</th>
                  </tr>
                </thead>                
              <tbody className="divide-y divide-background-100">
  {filtered.map(t => {
    const totalAPagar = t.asignaciones.reduce((sum, a) => sum + a.montoTotal, 0);
    const totalPagado = t.asignaciones.reduce(
      (sum, a) => sum + a.pagos.reduce((s, p) => s + p.monto, 0),
      0
    );

    return (
      <tr key={t.id} className="hover:bg-background-50/50 transition-colors">
        <td className="px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-secondary-700">{t.nombre.charAt(0)}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800">{t.nombre}</p>
              <p className="text-[11px] text-foreground-400 font-mono md:hidden">{t.rfc}</p>
            </div>
          </div>
        </td>
        <td className="px-5 py-3 hidden md:table-cell">
          <span className="text-xs text-foreground-600">{t.puesto}</span>
        </td>
        <td className="px-5 py-3">
          {t.asignaciones.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {t.asignaciones.map((a) => {
                const pagadoAsig = a.pagos.reduce((sum, p) => sum + p.monto, 0);
                const pendienteAsig = a.montoTotal - pagadoAsig;
                return (
                  <span
                    key={a.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDetalleAsignacion(t, a);
                    }}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors"
                    title={`${a.obraNombre} — Total: ${formatMonto(a.montoTotal)} / Pagado: ${formatMonto(pagadoAsig)} / Pendiente: ${formatMonto(pendienteAsig)}`}
                  >
                    <i className="ri-building-line text-[10px]" />
                    <span className="max-w-[160px] truncate">{a.obraNombre}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteAsignacionTarget(a);
                        setDeleteAsigWorker(t);
                      }}
                      className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-sm hover:bg-red-200 text-secondary-500 hover:text-red-600 transition-colors cursor-pointer flex-shrink-0"
                      title="Quitar obra"
                    >
                      <i className="ri-close-line text-[9px]" />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="text-xs text-foreground-400 italic">Sin obras asignadas</span>
          )}
        </td>
        <td className="px-5 py-3 text-right">
          <span className="text-sm font-semibold text-foreground-800">
            {formatMonto(totalAPagar)}
          </span>
        </td>
        <td className="px-5 py-3 text-right hidden md:table-cell">
          <span className="text-sm font-semibold text-green-600">
            {formatMonto(totalPagado)}
          </span>
        </td>
        <td className="px-5 py-3 text-right">
          <span className="text-sm font-semibold text-foreground-800">
            ${t.salarioDiario.toLocaleString('es-MX')}
          </span>
        </td>
        <td className="px-5 py-3">
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={() => openAsignar(t)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-100 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer"
              title="Asignar a proyecto"
            >
              <i className="ri-link-m text-sm" />
            </button>
            <button
              onClick={() => openEdit(t)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
              title="Editar trabajador"
            >
              <i className="ri-edit-line text-sm" />
            </button>
            <button
              onClick={() => setDeleteTarget(t)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
              title="Eliminar trabajador"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </td>
      </tr>
    );
  })}
</tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear/editar trabajador */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar trabajador' : 'Nuevo trabajador'}
        size="md"
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!form.nombre.trim()}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${!form.nombre.trim()
                  ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
            >
              {editingId ? 'Guardar cambios' : 'Registrar trabajador'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nombre completo *</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Juan Pérez López"
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Puesto</label>
              <input
                type="text"
                value={form.puesto}
                onChange={(e) => setForm({ ...form, puesto: e.target.value })}
                placeholder="Ej: Oficial Albañil"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Salario diario (MXN)</label>
              <input
                type="number"
                value={form.salarioDiario}
                onChange={(e) => setForm({ ...form, salarioDiario: e.target.value })}
                placeholder="Ej: 650"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">RFC</label>
              <input
                type="text"
                value={form.rfc}
                onChange={(e) => setForm({ ...form, rfc: e.target.value })}
                placeholder="Ej: PELJ850512XYZ"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 font-mono placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">NSS</label>
              <input
                type="text"
                value={form.nss}
                onChange={(e) => setForm({ ...form, nss: e.target.value })}
                placeholder="Ej: 12345678901"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 font-mono placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Fecha de ingreso</label>
            <input
              type="date"
              value={form.fechaIngreso}
              onChange={(e) => setForm({ ...form, fechaIngreso: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
        </div>
      </Modal>

      {/* Modal asignar obra */}
      <Modal
        open={asignarModalOpen}
        onClose={() => setAsignarModalOpen(false)}
        title={asignarTarget ? `Asignar obra — ${asignarTarget.nombre}` : 'Asignar obra'}
        size="sm"
        footer={
          <>
            <button
              onClick={() => setAsignarModalOpen(false)}
              className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleAsignarSave}
              disabled={!asignarForm.obraId || !asignarForm.montoTotal}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${!asignarForm.obraId || !asignarForm.montoTotal
                  ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
            >
              Asignar
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Obra (activa)</label>
            <select
              value={asignarForm.obraId}
              onChange={(e) => setAsignarForm({ ...asignarForm, obraId: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Seleccionar obra...</option>
              {obras.map((o: any) => (
                <option key={o.id} value={o.id}>
                  {o.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Monto total a pagar (MXN)</label>
            <input
              type="number"
              value={asignarForm.montoTotal}
              onChange={(e) => setAsignarForm({ ...asignarForm, montoTotal: e.target.value })}
              placeholder="Ej: 45000"
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
          {asignarTarget && asignarTarget.asignaciones.length > 0 && (
            <div className="bg-accent-50 rounded-lg p-3 border border-accent-200">
              <p className="text-xs text-accent-700 flex items-center gap-1.5">
                <i className="ri-information-line" />
                Este trabajador ya está en {asignarTarget.asignaciones.length} obra
                {asignarTarget.asignaciones.length !== 1 ? 's' : ''}. Se agregará una nueva asignación.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal detalle de pagos de asignación */}
      {detalleAsignacion && (
        <Modal
          open={detalleAsignacionOpen}
          onClose={() => setDetalleAsignacionOpen(false)}
          title={`Pagos: ${detalleAsignacion.asignacion.obraNombre}`}
          size="md"
          footer={null}
        >
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-xs text-foreground-500">Total a pagar</p>
                <p className="text-lg font-bold text-foreground-800">
                  {formatMonto(detalleAsignacion.asignacion.montoTotal)}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600">Total pagado</p>
                <p className="text-lg font-bold text-green-700">
                  {formatMonto(totalPagado(detalleAsignacion.asignacion))}
                </p>
              </div>
              <div
                className={`rounded-lg p-3 text-center ${saldoPendiente(detalleAsignacion.asignacion) > 0 ? 'bg-red-50' : 'bg-green-50'
                  }`}
              >
                <p className="text-xs text-foreground-500">Saldo pendiente</p>
                <p
                  className={`text-lg font-bold ${saldoPendiente(detalleAsignacion.asignacion) > 0 ? 'text-red-600' : 'text-green-600'
                    }`}
                >
                  {formatMonto(saldoPendiente(detalleAsignacion.asignacion))}
                </p>
              </div>
            </div>

            {/* Historial de pagos */}
            <div>
              <h4 className="text-sm font-semibold text-foreground-700 mb-2">Historial de pagos</h4>
              {detalleAsignacion.asignacion.pagos.length === 0 ? (
                <div className="text-center py-6 text-xs text-foreground-400 bg-background-50 rounded-lg">
                  No hay pagos registrados todavía.
                </div>
              ) : (
                <div className="border border-background-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-background-100/60">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-foreground-400 uppercase">Fecha</th>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-foreground-400 uppercase">Concepto</th>
                        <th className="text-right px-3 py-2 text-[10px] font-medium text-foreground-400 uppercase">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {detalleAsignacion.asignacion.pagos.map((p) => (
                        <tr key={p.id}>
                          <td className="px-3 py-2 text-foreground-500">
                            {new Date(p.fecha + 'T00:00:00').toLocaleDateString('es-MX', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </td>
                          <td className="px-3 py-2 text-foreground-700">{p.concepto}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-600">
                            {formatMonto(p.monto)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Formulario para nuevo pago */}
            <div className="border-t border-background-200 pt-4">
              <h4 className="text-sm font-semibold text-foreground-700 mb-3">Registrar nuevo pago</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Fecha</label>
                    <input
                      type="date"
                      value={pagoForm.fecha}
                      onChange={(e) => setPagoForm({ ...pagoForm, fecha: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Monto (MXN)</label>
                    <input
                      type="number"
                      value={pagoForm.monto}
                      onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })}
                      placeholder="Ej: 5000"
                      className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Concepto</label>
                  <input
                    type="text"
                    value={pagoForm.concepto}
                    onChange={(e) => setPagoForm({ ...pagoForm, concepto: e.target.value })}
                    placeholder="Ej: Pago parcial 1"
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleAgregarPago}
                    disabled={!pagoForm.monto || !pagoForm.concepto.trim()}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${!pagoForm.monto || !pagoForm.concepto.trim()
                        ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                      }`}
                  >
                    <i className="ri-add-line mr-1" />
                    Registrar pago
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirmación eliminar asignación */}
      <ConfirmDialog
        open={!!deleteAsignacionTarget}
        onClose={() => {
          setDeleteAsignacionTarget(null);
          setDeleteAsigWorker(null);
        }}
        onConfirm={handleEliminarAsignacion}
        title="Quitar obra"
        message={`¿Quitar "${deleteAsignacionTarget?.obraNombre}" de este trabajador? Los pagos registrados también se eliminarán.`}
        confirmLabel="Sí, quitar"
        variant="danger"
      />

      {/* Confirmación eliminar trabajador */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar trabajador"
        message={`¿Estás seguro de eliminar a "${deleteTarget?.nombre}"? También se eliminarán todas sus asignaciones a obras y los pagos registrados.`}
        confirmLabel="Sí, eliminar"
        variant="danger"
      />
    </DashboardLayout>
  );
}